/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.4.0 - Date Utilities
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file dateUtils.ts
 * @description Utilidades para manejo de fechas y calendarios
 * @version 1.0.0
 * @date 2025-01-27
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

// ═══════════════════════════════════════════════════════════════════════════
// FORMATO DE FECHAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Formatea una fecha según el patrón especificado
 */
export const formatDate = (date: Date | string, format: string = 'YYYY-MM-DD'): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) {
    return '';
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');

  return format
    .replace('YYYY', String(year))
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

/**
 * Formatea fecha a formato legible en español
 */
export const formatDateSpanish = (date: Date | string, includeTime: boolean = false): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  };

  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }

  return d.toLocaleDateString('es-CL', options);
};

/**
 * Formatea solo la hora
 */
export const formatTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) {
    return '';
  }

  return d.toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ═══════════════════════════════════════════════════════════════════════════
// MANIPULACIÓN DE FECHAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Agrega días a una fecha
 */
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Agrega meses a una fecha
 */
export const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

/**
 * Agrega años a una fecha
 */
export const addYears = (date: Date, years: number): Date => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
};

/**
 * Obtiene el primer día del mes
 */
export const startOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Obtiene el último día del mes
 */
export const endOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * Obtiene el inicio del día (00:00:00)
 */
export const startOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Obtiene el final del día (23:59:59)
 */
export const endOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

// ═══════════════════════════════════════════════════════════════════════════
// INFORMACIÓN DEL CALENDARIO
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Obtiene el número de días en un mes
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Obtiene el primer día de la semana del mes (0=Domingo, 1=Lunes, etc)
 */
export const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

/**
 * Obtiene el nombre del mes en español
 */
export const getMonthName = (month: number): string => {
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  return months[month] || '';
};

/**
 * Obtiene el nombre del día de la semana
 */
export const getDayName = (day: number): string => {
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return days[day] || '';
};

/**
 * Obtiene el nombre corto del día de la semana
 */
export const getDayNameShort = (day: number): string => {
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  return days[day] || '';
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPARACIONES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Verifica si dos fechas son el mismo día
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Verifica si una fecha es hoy
 */
export const isToday = (date: Date): boolean => {
  return isSameDay(date, new Date());
};

/**
 * Verifica si una fecha es del pasado
 */
export const isPast = (date: Date): boolean => {
  const now = new Date();
  return date < startOfDay(now);
};

/**
 * Verifica si una fecha es del futuro
 */
export const isFuture = (date: Date): boolean => {
  const now = new Date();
  return date > endOfDay(now);
};

/**
 * Verifica si una fecha es fin de semana
 */
export const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6; // Domingo o Sábado
};

/**
 * Verifica si una fecha está dentro de un rango
 */
export const isInRange = (date: Date, start: Date, end: Date): boolean => {
  return date >= start && date <= end;
};

// ═══════════════════════════════════════════════════════════════════════════
// DIFERENCIAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calcula la diferencia en días entre dos fechas
 */
export const daysDifference = (date1: Date, date2: Date): number => {
  const oneDay = 24 * 60 * 60 * 1000; // milliseconds
  const diff = Math.abs(date1.getTime() - date2.getTime());
  return Math.round(diff / oneDay);
};

/**
 * Calcula la diferencia en horas entre dos fechas
 */
export const hoursDifference = (date1: Date, date2: Date): number => {
  const oneHour = 60 * 60 * 1000; // milliseconds
  const diff = Math.abs(date1.getTime() - date2.getTime());
  return Math.round(diff / oneHour);
};

/**
 * Calcula la diferencia en minutos entre dos fechas
 */
export const minutesDifference = (date1: Date, date2: Date): number => {
  const oneMinute = 60 * 1000; // milliseconds
  const diff = Math.abs(date1.getTime() - date2.getTime());
  return Math.round(diff / oneMinute);
};

// ═══════════════════════════════════════════════════════════════════════════
// PARSEO Y VALIDACIÓN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Parsea una fecha en formato YYYY-MM-DD
 */
export const parseDate = (dateString: string): Date | null => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Combina fecha y hora en un Date
 */
export const combineDateAndTime = (date: string, time: string): Date => {
  return new Date(`${date}T${time}:00`);
};

/**
 * Valida si una fecha es válida
 */
export const isValidDate = (date: any): boolean => {
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Valida formato de fecha YYYY-MM-DD
 */
export const isValidDateString = (dateString: string): boolean => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;
  
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Valida formato de hora HH:mm
 */
export const isValidTimeString = (timeString: string): boolean => {
  const regex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
  return regex.test(timeString);
};

// ═══════════════════════════════════════════════════════════════════════════
// UTILIDADES DE CALENDARIO
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Genera array de fechas para un mes completo (incluyendo días del mes anterior/siguiente)
 */
export const generateCalendarDays = (year: number, month: number): Date[] => {
  const firstDay = getFirstDayOfMonth(year, month);
  const daysInMonth = getDaysInMonth(year, month);
  const days: Date[] = [];

  // Días del mes anterior
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
  
  for (let i = firstDay - 1; i >= 0; i--) {
    days.push(new Date(prevYear, prevMonth, daysInPrevMonth - i));
  }

  // Días del mes actual
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(year, month, i));
  }

  // Días del mes siguiente (para completar semanas)
  const remainingDays = 42 - days.length; // 6 semanas x 7 días = 42
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  
  for (let i = 1; i <= remainingDays; i++) {
    days.push(new Date(nextYear, nextMonth, i));
  }

  return days;
};

/**
 * Obtiene la semana del año
 */
export const getWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

// ═══════════════════════════════════════════════════════════════════════════
// TIEMPO RELATIVO
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Obtiene tiempo relativo (hace X minutos, hace X horas, etc)
 */
export const getRelativeTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Ahora';
  if (minutes < 60) return `Hace ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  if (hours < 24) return `Hace ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  if (days < 7) return `Hace ${days} ${days === 1 ? 'día' : 'días'}`;
  if (days < 30) return `Hace ${Math.floor(days / 7)} ${Math.floor(days / 7) === 1 ? 'semana' : 'semanas'}`;
  if (days < 365) return `Hace ${Math.floor(days / 30)} ${Math.floor(days / 30) === 1 ? 'mes' : 'meses'}`;
  return `Hace ${Math.floor(days / 365)} ${Math.floor(days / 365) === 1 ? 'año' : 'años'}`;
};

/**
 * Obtiene duración en formato legible
 */
export const formatDuration = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export default {
  formatDate,
  formatDateSpanish,
  formatTime,
  addDays,
  addMonths,
  addYears,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  getDaysInMonth,
  getFirstDayOfMonth,
  getMonthName,
  getDayName,
  getDayNameShort,
  isSameDay,
  isToday,
  isPast,
  isFuture,
  isWeekend,
  isInRange,
  daysDifference,
  hoursDifference,
  minutesDifference,
  parseDate,
  combineDateAndTime,
  isValidDate,
  isValidDateString,
  isValidTimeString,
  generateCalendarDays,
  getWeekNumber,
  getRelativeTime,
  formatDuration
};
